import React, { useState } from 'react'
import { useT } from '@transifex/react'
import { Button, Header, Box } from '@lightspeed/design-system-react'
import { VendImage, useIsMounted } from '@vend/react-utilities'
import classNames from 'classnames'
import { noWidow } from '@vend/utilities'
import {
  useCashMovementsManager,
  useRegisterClosureManager,
  useRegisterManager,
} from '../../ng-compat/hooks'
import { STATES } from '../../../../lib/routes'
import { $state } from '../../ng-compat/imports'
import { CashFloatForm } from '../cash-management/CashFloatForm'
import { CashFloatSettings } from '../cash-management/utils/types'

interface Props {
  minimal?: boolean
}

export const OpenRegisterPanel: React.FC<Props> = ({ minimal = false }) => {
  const t = useT()
  const isMountedRef = useIsMounted()

  const cashManagementEnabled = useCashMovementsManager().isCashManagementEnabled()
  const activeRegister = useRegisterManager().getActiveRegister()
  const registerClosureManager = useRegisterClosureManager()

  const [loading, setLoading] = useState(false)
  const [hasValidationErrors, setHasValidationErrors] = useState(false)
  const onError = (hasErrors: boolean) => setHasValidationErrors(hasErrors)
  const openRegister = (settings?: CashFloatSettings) => {
    setLoading(true)
    registerClosureManager
      .openRegister(activeRegister, settings)
      .then(() => $state.go(STATES.SALE))
      .catch(() => {
        if (isMountedRef.current) {
          setLoading(false)
        }
      })
  }

  return (
    <Box className="open-register-panel-container open-register-panel-container--boxed vd-pb10">
      <Box className="open-register-panel vd-pb2">
        {!minimal && (
          <Box className="open-register-panel-header vd-align-center">
            <VendImage
              className="wr-register-closed-image"
              relativeImagePath="sell/register-closed-v2.svg"
              relativeDarkImagePath="sell/register-closed-v2-dark.svg"
            />
            <Header level="2" type="heading" className="vd-mt3 vd-mb3">
              {t('Register closed', { _context: 'Section Header' })}
            </Header>
          </Box>
        )}
        {!registerClosureManager.hasOpenClosePermission() ? (
          <p>
            You don't have permission to open the register. Switch to a user
            with permission.
          </p>
        ) : (
          <>
            <p className="vd-p pro-open-register-intro">
              {cashManagementEnabled
                ? noWidow(
                    t(
                      'Set an opening float to open the register and make a sale.'
                    )
                  )
                : noWidow(t('To make a sale, please open the register.'))}
            </p>
            {cashManagementEnabled ? (
              <CashFloatForm
                onSubmit={openRegister}
                onError={onError}
                noteRows={2}
                className={classNames({
                  'open-register-panel-form--minimal': minimal,
                })}
              >
                <Button
                  type="submit"
                  disabled={hasValidationErrors}
                  loading={loading}
                  className="open-register-panel-button"
                  data-track="open-register"
                >
                  {t('Open Register', { _context: 'Button' })}
                </Button>
              </CashFloatForm>
            ) : (
              <Button
                onClick={() => openRegister()}
                type="button"
                loading={loading}
                className="open-register-panel-button"
                data-track="open-register"
              >
                {t('Open Register', { _context: 'Button' })}
              </Button>
            )}
          </>
        )}
      </Box>
    </Box>
  )
}
